import { useAtomValue, useStore as useJotaiStore, useSetAtom } from 'jotai';
import { type MutableRefObject, useEffect } from 'react';

import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import {
  CONNECTION_STATUS,
  chronicleDeviceRecordingInfoAtom,
  chronicleDeviceStateAtom,
  chronicleLiveOfflineAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleRecordingEndReasonAtom,
  chronicleSessionIdAtom,
} from '@/atoms/chronicle';
import { logHardwareDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { stripDeviceFileNameForAnalytics } from '@/utils/analytics/strip-device-file-name';

// A held live-offline waits for a reconnect that may never come (device
// powered off, battery dead, left behind). Past this deadline the hold
// resolves exactly like the STE&0 exit: an interrupted finalize, so the
// device-stop detector runs the interrupted stop and bulk-sync recovery owns
// the on-device audio. A reconnect after the deadline still recovers via the
// recorded-file bind, so the cost of expiring is a resumable interruption —
// the cost of never expiring is a locked app.
const LIVE_OFFLINE_HOLD_TIMEOUT_MS = 120_000;

// While the device is CONNECTED the recovery handshake owns the hold — the
// deadline defers on this interval and only ever finalizes against a device
// that is currently gone. A handshake stalled while connected is a different
// (pre-existing) failure family and is not this hatch's to force.
const HOLD_CONNECTED_RECHECK_MS = 30_000;

/**
 * Escape hatch for a live-offline hold whose reconnect never arrives: after
 * `LIVE_OFFLINE_HOLD_TIMEOUT_MS` (anchored to the hold's own `anchorMs`, so a
 * provider remount mid-hold does not restart the clock) the hold is released
 * with the same atom writes the provider's finalize branch performs, which
 * lets the existing device-stop detector run the interrupted stop.
 *
 * Mounted once by ChronicleStateProvider; `wasRecordingRef` is the provider's
 * recording-transition ref, shared with the state_changed handler.
 */
export function useChronicleLiveOfflineHoldTimeout(
  wasRecordingRef: MutableRefObject<boolean>
) {
  const liveOffline = useAtomValue(chronicleLiveOfflineAtom);
  const setLiveOffline = useSetAtom(chronicleLiveOfflineAtom);
  const setRecordingEndReason = useSetAtom(chronicleRecordingEndReasonAtom);
  const setDeviceRecordingInfo = useSetAtom(chronicleDeviceRecordingInfoAtom);
  const setReconnectedPendingEnd = useSetAtom(chronicleReconnectedPendingEndAtom);
  const jotaiStore = useJotaiStore();

  useEffect(() => {
    if (!liveOffline) return;

    const elapsedMs = Date.now() - liveOffline.anchorMs;
    const remainingMs = Math.max(0, LIVE_OFFLINE_HOLD_TIMEOUT_MS - elapsedMs);

    let timer: ReturnType<typeof setTimeout>;

    const fire = () => {
      // Identity, not just presence: a stale timer must never resolve a
      // NEWER session's hold (released-then-re-held in the same window).
      if (jotaiStore.get(chronicleLiveOfflineAtom) !== liveOffline) return;

      // Device back: the recovery handshake (reconnect → APP&STE →
      // recording_recovered) owns the hold — never finalize under it, only
      // against a device that is currently gone.
      const isDeviceConnected =
        jotaiStore.get(chronicleDeviceStateAtom).connectionStatus ===
        CONNECTION_STATUS.CONNECTED;
      if (isDeviceConnected) {
        timer = setTimeout(fire, HOLD_CONNECTED_RECHECK_MS);
        return;
      }

      // The device-stop detector that this finalize trips requires a truthy
      // `chronicleSessionIdAtom`. A hold armed before the backend session
      // existed (`liveOffline.sessionId === null` and session atom still null)
      // therefore tears the UI down without ever running stop — surface that
      // as its own recovery_mode so it cannot be confused with a held_timeout
      // that did trigger the interrupted stop (`cleanAttributes` also strips
      // null session_id, so the discriminator must not depend on it).
      const chronicleSessionId = jotaiStore.get(chronicleSessionIdAtom);
      const heldForMs = Date.now() - liveOffline.anchorMs;
      const hasSessionToStop = chronicleSessionId !== null;

      if (hasSessionToStop) {
        logger.warn(
          '[useChronicleLiveOfflineHoldTimeout] hold timed out — finalizing as interrupted',
          {
            sessionId: liveOffline.sessionId,
            chronicleSessionId,
            heldForMs,
          }
        );
        // An expired hold and a device-reported BLE drop both terminalise as
        // `ble_disconnect`, which makes them indistinguishable in the interrupted
        // telemetry — and this expiry path IS the failure class being fixed, so
        // it has to be countable on its own. `recovery_mode` is the same
        // discriminator `logRecordingInterruptedHold` uses to mark hold entry.
        logHardwareDatadogEvent(
          'hardware_recording_interrupted',
          {
            session_id: liveOffline.sessionId ?? chronicleSessionId,
            session_id_local: liveOffline.sessionId ?? chronicleSessionId,
            device_file_name: stripDeviceFileNameForAnalytics(liveOffline.fileName),
            reason: 'ble_disconnect',
            recovery_mode: 'held_timeout',
            held_for_ms: heldForMs,
            has_session_to_stop: true,
          },
          'warn'
        );
      } else {
        logger.warn(
          '[useChronicleLiveOfflineHoldTimeout] hold timed out with no session to stop — UI teardown only',
          {
            sessionId: liveOffline.sessionId,
            fileName: liveOffline.fileName,
            heldForMs,
          }
        );
        logHardwareDatadogEvent(
          'hardware_recording_interrupted',
          {
            session_id: liveOffline.sessionId,
            session_id_local: liveOffline.sessionId,
            device_file_name: stripDeviceFileNameForAnalytics(liveOffline.fileName),
            reason: 'ble_disconnect',
            recovery_mode: 'held_timeout_no_session',
            held_for_ms: heldForMs,
            has_session_to_stop: false,
          },
          'warn'
        );
      }

      setLiveOffline(null);
      setRecordingEndReason('ble_disconnect');
      // The expiring deadline also ends any reconnect window (a hold after a
      // recovered bind re-arms it) — left set, the device-stop detector's
      // recovery-window guard would swallow the very stop this finalize
      // exists to trigger. Keyed on BOTH the hold's sessionId AND the session
      // atom the detector compares against: a hold armed with
      // `liveOffline.sessionId === null` while the window was armed for the
      // chronicle session id would otherwise leave suppression ON forever.
      const pendingEnd = jotaiStore.get(chronicleReconnectedPendingEndAtom);
      if (
        pendingEnd !== null &&
        (pendingEnd === liveOffline.sessionId || pendingEnd === chronicleSessionId)
      ) {
        setReconnectedPendingEnd(null);
      }
      setDeviceRecordingInfo(null);
      wasRecordingRef.current = false;
    };

    timer = setTimeout(fire, remainingMs);

    return () => clearTimeout(timer);
  }, [
    liveOffline,
    jotaiStore,
    setLiveOffline,
    setRecordingEndReason,
    setReconnectedPendingEnd,
    setDeviceRecordingInfo,
    wasRecordingRef,
  ]);
}
